// All player state in 1 64 bit bigint
// 0000-0000-0000-0000-0000-0000-0000-0000
// // [I]D, [P]oints, [D]irection, [S]tate, [H]ealth, [M]ap, [X], [Y]
// IIII-IIII-IIII-IIII-IIII-PPPP-PPPP-PPPP-DDSS-SSHH-HHMM-MMXX-XXXX-XXXY-YYYY-YYYY

import {
  player_classname,
  PlayerDirection,
  PlayerState,
} from "./css-names/player-css-names";
import { playerHeightPx, playerWidthPx } from "./scale";

// Direction bitmask: & 1100_0000_0000_0000_0000_0000_0000_0000
// 0000_0000 UP    Bitmask & 0000_0000
// 0100_0000 DOWN
// 1000_0000 LEFT
// 1100_0000 RIGHT

// State bitmask: & 0011_1000_0000_0000_0000_0000_0000_0000
// 0000_0000 IDLE
// 0001_0000 WALK
// 0010_0000 ATTACK
// 0011_0000 HIT
// 0000_1000 SPAWN

// Health bitmask & 0000_0111_1000

const ID_MASK =
  0b1111_1111_1111_1111_1111_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000n;
export const idFromState = (state: bigint): number =>
  Number((state & ID_MASK) >> 44n);

const DIRECTION_MASK =
  0b0000_0000_0000_0000_0000_0000_0000_0000_1100_0000_0000_0000_0000_0000_0000_0000n;
export const UP =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000n;
export const DOWN =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0100_0000_0000_0000_0000_0000_0000_0000n;
export const LEFT =
  0b0000_0000_0000_0000_0000_0000_0000_0000_1000_0000_0000_0000_0000_0000_0000_0000n;
export const RIGHT =
  0b0000_0000_0000_0000_0000_0000_0000_0000_1100_0000_0000_0000_0000_0000_0000_0000n;
export const directionFromState = (state: bigint): PlayerDirection => {
  const bigIntDirection = state & DIRECTION_MASK;
  switch (bigIntDirection) {
    case UP:
      return PlayerDirection.UP;
    case DOWN:
      return PlayerDirection.DOWN;
    case LEFT:
      return PlayerDirection.LEFT;
    case RIGHT:
      return PlayerDirection.RIGHT;
    default:
      return PlayerDirection.DOWN;
  }
};

const PLAYER_STATE_MASK =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0011_1100_0000_0000_0000_0000_0000_0000n;
export const IDLE =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000n;
export const WALK =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0001_0000_0000_0000_0000_0000_0000_0000n;
export const ATTACK =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0010_0000_0000_0000_0000_0000_0000_0000n;
export const HIT =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0011_0000_0000_0000_0000_0000_0000_0000n;
export const SPAWN =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0000_1000_0000_0000_0000_0000_0000_0000n;
export const BUMP =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0001_1000_0000_0000_0000_0000_0000_0000n;

export const playerStateFromState = (state: bigint): PlayerState => {
  const bigIntDirection = state & PLAYER_STATE_MASK;
  switch (bigIntDirection) {
    case IDLE:
      return PlayerState.IDLE;
    case WALK:
      return PlayerState.WALK;
    case ATTACK:
      return PlayerState.ATTACK;
    case HIT:
      return PlayerState.HIT;
    case SPAWN:
      return PlayerState.SPAWN;
    default:
      return PlayerState.IDLE;
  }
};

const transformMap = {
  [PlayerDirection.DOWN]: "translate3d(0px, 0px, 0px)",
  [PlayerDirection.RIGHT]: "translate3d(0px, -24px, 0px)",
  [PlayerDirection.LEFT]: "translate3d(0px, -48px, 0px)",
  [PlayerDirection.UP]: "translate3d(0px, -72px, 0px)",
};

// Health is 4 bits (0-15)
const HEALTH_BITMASK =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0000_0011_1100_0000_0000_0000_0000_0000n;
const healthFromState = (state: bigint) =>
  Number((state & HEALTH_BITMASK) >> 22n);

const POINTS_BITMASK =
  0b0000_0000_0000_0000_0000_1111_1111_1111_0000_0000_0000_0000_0000_0000_0000_0000n;
const pointsFromState = (state: bigint) =>
  Number((state & POINTS_BITMASK) >> 32n);

export const MAX_HEALTH = 15;
export const MAX_POINTS = 4095;

const findOrCreateElement = (
  id: number,
  layer: HTMLDivElement,
): { element: HTMLDivElement; sprite: HTMLImageElement } => {
  let element: HTMLDivElement = document.getElementById(
    `player_${id}`,
  ) as HTMLDivElement;
  if (element) {
    return {
      element: element as HTMLDivElement,
      sprite: element.querySelector("img") as HTMLImageElement,
    };
  }

  element = document.createElement("div") as HTMLDivElement;
  element.id = `player_${id}`;
  element.classList.add(player_classname);
  const sprite = document.createElement("img") as HTMLImageElement;
  sprite.src = "/img/player-sprite.png";
  element.appendChild(sprite);
  layer.appendChild(element);
  return { element, sprite };
};

const X_BITMASK =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0011_1111_1110_0000_0000n;
const Y_BITMASK =
  0b0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0000_0001_1111_1111n;

const xFromState = (state: bigint) => Number((state & X_BITMASK) >> 9n);
const yFromState = (state: bigint) => Number(state & Y_BITMASK);

export class Player {
  health: number = 0xb1111;
  points: number = 0;
  name: string = "PLAYER";
  direction: PlayerDirection = PlayerDirection.DOWN;
  playerState: PlayerState = PlayerState.IDLE;
  x: number;
  y: number;
  id: number;
  element: HTMLDivElement;
  sprite: HTMLImageElement;
  isLocal: boolean = false;

  constructor(
    public state: bigint,
    private playerLayer: HTMLDivElement,
  ) {
    this.id = idFromState(state);
    this.direction = directionFromState(state);
    this.playerState = playerStateFromState(state);
    this.health = healthFromState(state);
    this.points = pointsFromState(state);
    this.x = xFromState(state) * playerWidthPx;
    this.y = yFromState(state) * playerHeightPx;
    const { element, sprite } = findOrCreateElement(this.id, this.playerLayer);
    this.element = element;
    this.sprite = sprite;

    // Determine if this player is the local player by reading the cookie set during "Go Online"
    const localCookieMatch = document.cookie.match(/(?:^|; )player_id=([^;]+)/);
    const localId = localCookieMatch
      ? Number(decodeURIComponent(localCookieMatch[1]!))
      : NaN;
    this.isLocal = Number.isFinite(localId) && localId === this.id;
    if (this.isLocal) {
      this.element.classList.add("is-local");
      this.ensureHudElements();
      this.updateHud();
    }

    // Initialize bars (CSS variables piggy-back on player transform)
    this.updateBars();

    this.updatePlayerState(this.playerState, this.direction);
    this.updateCoordinates(this.x, this.y);
  }
  setState(state: bigint) {
    if (this.state === state) return;

    const nextDir = directionFromState(state);
    const nextState = playerStateFromState(state);
    if (nextState !== this.playerState || this.direction !== nextDir) {
      this.updatePlayerState(nextState, nextDir);
    }
    this.health = healthFromState(state);
    this.points = pointsFromState(state);

    // Update visual bars when health/points change
    this.updateBars();
    if (this.isLocal) {
      this.updateHud();
    }

    const nextX = xFromState(state) * playerWidthPx;
    const nextY = yFromState(state) * playerHeightPx;
    if (nextX !== this.x || nextY !== this.y) {
      this.updateCoordinates(nextX, nextY);
    }
    this.state = state;
  }
  private updatePlayerState(nextState: PlayerState, nextDir: PlayerDirection) {
    console.log(nextDir, nextState);
    this.direction = nextDir;
    this.playerState = nextState;
    switch (nextState) {
      case PlayerState.IDLE: {
        this.sprite.style.animation = "";
        this.sprite.style.transform = transformMap[this.direction];
        break;
      }
      case PlayerState.WALK: {
        this.sprite.style.animation = `1s infinite steps(4) ${nextState}-${nextDir}`;
        break;
      }
      case PlayerState.ATTACK: {
        this.sprite.style.animation = `300ms steps(4) ${nextState}-${nextDir}`;
        break;
      }
      case PlayerState.SPAWN: {
        this.sprite.style.animation = "500ms steps(5) player-spawn";
        break;
      }
      default:
        break;
    }
    // this.element.classList.remove(
    //   PlayerState.ATTACK,
    //   PlayerState.HIT,
    //   PlayerState.WALK,
    //   PlayerState.SPAWN,
    //   PlayerState.IDLE,
    //   PlayerDirection.UP,
    //   PlayerDirection.DOWN,
    //   PlayerDirection.LEFT,
    //   PlayerDirection.RIGHT,
    // );
    // this.element.classList.add(this.playerState, this.direction);
  }
  private updateCoordinates(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.element.style.transform = `translate3d(${this.x - playerWidthPx}px,${this.y - playerHeightPx}px,0)`;
  }

  // Create HUD overlay containers if missing (structure only; styling via CSS and simple divs)
  private ensureHudElements() {
    let overlay = document.getElementById(
      "hud-overlay",
    ) as HTMLDivElement | null;
    if (!overlay) {
      const screen = document.getElementById("screen") as HTMLDivElement | null;
      overlay = document.createElement("div");
      overlay.id = "hud-overlay";
      (screen ?? document.body).appendChild(overlay);
    }

    // Remove old content if present (to support live reloads/hot dev)
    overlay.innerHTML = "";

    // -- Health Row --
    const hudHealth = document.createElement("div");
    hudHealth.id = "hud-health";

    const healthLabel = document.createElement("div");
    healthLabel.className = "hud-label";
    healthLabel.textContent = "HP";

    const healthBar = document.createElement("div");
    healthBar.className = "hud-bar";
    const healthFill = document.createElement("div");
    healthFill.className = "fill";
    healthFill.style.width = "0";
    healthBar.appendChild(healthFill);

    const healthVal = document.createElement("div");
    healthVal.className = "hud-val";
    healthVal.textContent = String(this.health);

    hudHealth.appendChild(healthLabel);
    hudHealth.appendChild(healthBar);
    hudHealth.appendChild(healthVal);
    overlay.appendChild(hudHealth);

    // -- Points Row --
    const hudPoints = document.createElement("div");
    hudPoints.id = "hud-points";

    const pointsLabel = document.createElement("div");
    pointsLabel.className = "hud-label";
    pointsLabel.textContent = "PTS";

    const pointsBar = document.createElement("div");
    pointsBar.className = "hud-bar";

    const pointsFill = document.createElement("div");
    pointsFill.className = "fill";
    pointsBar.appendChild(pointsFill);

    const pointsVal = document.createElement("div");
    pointsVal.className = "hud-val";
    pointsVal.textContent = String(this.points);

    hudPoints.appendChild(pointsLabel);
    hudPoints.appendChild(pointsBar);
    hudPoints.appendChild(pointsVal);
    overlay.appendChild(hudPoints);
  }

  // Update HUD values (local player)
  private updateHud() {
    const hudHealth = document.getElementById(
      "hud-health",
    ) as HTMLDivElement | null;
    if (hudHealth) {
      const bar = hudHealth.querySelector(".hud-bar") as HTMLDivElement | null;
      const fill = bar && (bar.querySelector(".fill") as HTMLDivElement | null);
      const val = hudHealth.querySelector(".hud-val") as HTMLDivElement | null;
      if (fill) {
        const pct = Math.max(0, Math.min(1, this.health / MAX_HEALTH));
        fill.style.width = `${pct * 100}%`;
      }
      if (val) val.textContent = String(this.health);
    }

    const hudPoints = document.getElementById(
      "hud-points",
    ) as HTMLDivElement | null;
    if (hudPoints) {
      const bar = hudPoints.querySelector(".hud-bar") as HTMLDivElement | null;
      const fill = bar && (bar.querySelector(".fill") as HTMLDivElement | null);
      const val = hudPoints.querySelector(".hud-val") as HTMLDivElement | null;
      if (fill) {
        const pct = Number(
          Math.max(0, Math.min(1, this.points / MAX_POINTS)).toFixed(2),
        );
        fill.style.width = `${pct * 100}%`;
      }
      if (val) val.textContent = String(this.points);
    }
  }

  // Update per-player floating bars via CSS variables (piggy-backs on player transform)
  private updateBars() {
    // Expose values as CSS variables so tiny bars can be rendered via CSS (::before/::after) or external stylesheet
    this.element.style.setProperty("--hp", String(this.health));
    this.element.style.setProperty("--hp-max", String(MAX_HEALTH));
    this.element.style.setProperty("--pts", String(this.points));
    this.element.style.setProperty("--pts-max", String(MAX_POINTS));
  }
}
